<template>
  <div>
    <h2 class="table-title">{{ $t('offeredJobs.title') }}</h2>
    <!-- Mobil Kart Görünüm -->
      <div class="mobile-job-cards">
        <div
          class="mobile-card"
          v-for="offer in paginatedOffers"
          :key="'card-' + offer.id"
        >
          <p><strong>{{ $t('jobCode') }}:</strong> {{ formatJobCode(offer.id) }}</p>
          <p><strong>{{ $t('file') }}:</strong> {{ getOriginalFileName(offer.file_name) }}</p>
          <p><strong>{{ $t('desc') }}:</strong> {{ offer.description || '-' }}</p>
          <p><strong>{{ $t('languages') }}:</strong> {{ offer.source_lang }} → {{ offer.target_lang }}</p>
          <p><strong>{{ $t('specialization') }}:</strong> {{ offer.specialization || '-' }}</p>
          <p><strong>{{ $t('offerPrice') }}:</strong> ₺{{ offer.offer_price }}</p>
          <p><strong>{{ $t('offerDeliveryDate') }}:</strong> {{ formatDate(offer.offer_delivery_date) }}</p>
          <p><strong>{{ $t('customer') }}:</strong> {{ offer.user_name }} {{ offer.user_surname }}</p>
        </div>
      </div>

    <div class="table-wrapper">
      
      <table class="job-table">
        <thead>
          <tr>
            <th @click="sortBy('id')" class="sortable">{{ $t('jobCode') }}</th>
            <th @click="sortBy('file_name')" class="sortable">{{ $t('file') }}</th>
            <th @click="sortBy('description')" class="sortable">{{ $t('desc') }}</th>
            <th @click="sortBy('source_lang')" class="sortable">{{ $t('languages') }}</th>
            <th @click="sortBy('specialization')" class="sortable">{{ $t('specialization') }}</th>
            <th @click="sortBy('offer_price')" class="sortable">{{ $t('offerPrice') }}</th>
            <th @click="sortBy('offer_delivery_date')" class="sortable">{{ $t('offerDeliveryDate') }}</th>
            <th @click="sortBy('user_name')" class="sortable">{{ $t('customer') }}</th>
          </tr>
        </thead>
        <tbody>
        <template v-for="offer in paginatedOffers" :key="offer.id">
          <!-- Ana Satır -->
          <tr @click="toggleOfferDetails(offer.id)" style="cursor: pointer;">
            <td>{{ formatJobCode(offer.id) }}</td>
            <td>{{ getOriginalFileName(offer.file_name) }}</td>
            <td>{{ offer.description || '-' }}</td>
            <td>{{ offer.source_lang }} → {{ offer.target_lang }}</td>
            <td>{{ offer.specialization || '-' }}</td>
            <td>₺{{ offer.offer_price }}</td>
            <td>{{ formatDate(offer.offer_delivery_date) }}</td>
            <td>{{ offer.user_name }} {{ offer.user_surname }}</td>
          </tr>

          <!-- Detay Satırı -->
          <tr v-if="expandedOfferId === offer.id">
            <td colspan="8">
              <div class="job-details">
                <p><strong>{{ $t('jobCode') }}:</strong> {{ formatJobCode(offer.id) }}</p>
                <p><strong>{{ $t('file') }}:</strong> {{ getOriginalFileName(offer.file_name) }}</p>
                <p><strong>{{ $t('desc') }}:</strong> {{ offer.description || '-' }}</p>
                <p><strong>{{ $t('languages') }}:</strong> {{ offer.source_lang }} → {{ offer.target_lang }}</p>
                <p><strong>{{ $t('specialization') }}:</strong> {{ offer.specialization || '-' }}</p>
                <p><strong>{{ $t('offerPrice') }}:</strong> ₺{{ offer.offer_price }}</p>
                <p><strong>{{ $t('offerDeliveryDate') }}:</strong> {{ formatDate(offer.offer_delivery_date) }}</p>
                <p><strong>{{ $t('customer') }}:</strong> {{ offer.user_name }} {{ offer.user_surname }}</p>
              </div>
            </td>
          </tr>
        </template>
      </tbody>
      </table>
    </div>   
    <!-- Sayfalama -->
    <div class="pagination">
      <button @click="prevPage" :disabled="page === 1">‹</button>
      <span>{{ $t('page') }} {{ page }} / {{ totalPages }}</span>
      <button @click="nextPage" :disabled="page >= totalPages">›</button>
    </div>
      </div>
</template>

<script setup>
import { defineProps, ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
useI18n()


const props = defineProps({
  offers: {
    type: Array,
    default: () => []
  }
})

const page = ref(1)
const pageSize = 10

const sortKey = ref('')
const sortDirection = ref(1) // 1: ASC, -1: DESC
const expandedOfferId = ref(null)

function toggleOfferDetails(id) {
  expandedOfferId.value = expandedOfferId.value === id ? null : id
}
function formatJobCode(id) {
  return 'JOB-' + String(id).padStart(6, '0')
}

function getOriginalFileName(fileName) {
  if (!fileName) return '-'
  const parts = fileName.split('-')
  return parts.slice(2).join('-') || fileName
}

function formatDate(dateStr) {
  if (!dateStr) return '-'
  return dateStr.substring(0, 10)
}

function sortBy(key) {
  if (sortKey.value === key) {
    sortDirection.value *= -1
  } else {
    sortKey.value = key
    sortDirection.value = 1
  }
}

// ✅ Sıralı teklifler
const sortedOffers = computed(() => {
  const list = [...props.offers]
  if (!sortKey.value) return list
  return list.sort((a, b) => {
    const valA = a[sortKey.value] || ''
    const valB = b[sortKey.value] || ''
    return valA < valB ? -1 * sortDirection.value : valA > valB ? 1 * sortDirection.value : 0
  })
})

// ✅ Sayfalanmış teklifler
const paginatedOffers = computed(() => {
  const start = (page.value - 1) * pageSize
  return sortedOffers.value.slice(start, start + pageSize)
})

const totalPages = computed(() => Math.ceil(sortedOffers.value.length / pageSize))

function nextPage() {
  if (page.value < totalPages.value) page.value++
}

function prevPage() {
  if (page.value > 1) page.value--
}
</script>

<style scoped>
.table-title {
  font-size: 1.6rem;
  font-weight: 700;       /* daha kalın */
  margin-bottom: 20px;
  margin-top: 30px;
  color: #2c3e50;
  text-align: center;     /* ortalama */
}

.job-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 30px;
}

.job-table th, .job-table td {
  padding: 12px;
  border-bottom: 1px solid #ccc;
  text-align: left;
}

.job-table th {
  background-color: #f5f5f5;
  font-weight: bold;
}
.sortable {
  cursor: pointer;
  user-select: none;
  transition: background-color 0.2s ease;
}

.sortable:hover {
  background-color: #f0f4ff;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  margin-top: 15px;
}

.pagination button {
  padding: 6px 12px;
  border: none;
  border-radius: 6px;
  background-color: #3498db;
  color: white;
  cursor: pointer;
}

.pagination button:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}
.table-wrapper {
  width: 100%;
  overflow-x: auto;
}

.table-wrapper table {
  min-width: 900px;
}
.mobile-job-cards {
  display: none; /* Mobil kartlar GİZLİ */
}

/* Mobil kart görünümü */
@media (max-width: 768px) {
  .table-wrapper {
    display: none;
  }

  .mobile-job-cards {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 10px;
  }

  .mobile-card {
    background: #fff;
    border-radius: 10px;
    box-shadow: 0 0 8px rgba(0, 0, 0, 0.08);
    padding: 14px;
    font-size: 14px;
  }

  .mobile-card p {
    margin: 6px 0;
    line-height: 1.4;
  }

  .mobile-card a {
    color: #3498db;
    text-decoration: underline;
    word-break: break-word;
  }
}

</style>



